import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin } from 'rxjs';
import { LearningResourceAchievementFormDialogComponent } from 'src/app/components/main/employees/rrhh/learning-resource-achievement-form-dialog/learning-resource-achievement-form-dialog.component';
import { LearningResourceAchievement } from 'src/app/models/learning_resource_achievement.model';
import { LearningResourceEnrollment, LearningResourceEnrollmentSerialized } from 'src/app/models/learning_resource_enrollment.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';
import { CompletedAgoPipe } from 'src/app/pipes/completed-ago.pipe';

export interface LearningResourceUserFormationProfileLoadedEvent {
  active_enrollments: LearningResourceEnrollment[];
}

@Component({
  selector: 'app-learning-resource-user-formation-profile',
  templateUrl: './learning-resource-user-formation-profile.component.html',
  styleUrl: './learning-resource-user-formation-profile.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CompletedAgoPipe, DatePipe, MatButtonModule, MatCardModule, MatIconModule, MatTableModule, MatTooltipModule],
})
export class LearningResourceUserFormationProfileComponent implements OnInit {
  readonly user_id = input.required<number>();
  readonly enable_achievement_management = input(false);

  readonly formation_profile_loaded = output<LearningResourceUserFormationProfileLoadedEvent>();

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly snack_bar = inject(MatSnackBar);

  readonly loading = signal(true);
  readonly active_enrollments = signal<LearningResourceEnrollment[]>([]);
  readonly profile_user = signal<User | null>(null);

  enrollments_data_source = new MatTableDataSource<LearningResourceEnrollmentSerialized>([]);
  achievements_data_source = new MatTableDataSource<LearningResourceAchievement>([]);

  enrollments_displayed_columns: string[] = ['resource', 'period', 'hours', 'status'];
  achievements_displayed_columns: string[] = ['resource', 'period', 'completed_ago'];

  ngOnInit(): void {
    this.loadFormationProfile();
  }

  reload(): void {
    this.loadFormationProfile();
  }

  completeActiveEnrollment(enrollment: LearningResourceEnrollment): void {
    const user_id = this.user_id();
    const resource_name = enrollment.learning_resource?.name;

    if (!resource_name) {
      return;
    }

    if (!confirm(`¿Marcar "${resource_name}" como curso completado? Se cerrará esta matrícula activa.`)) {
      return;
    }

    this.api_service
      .completeActiveLearningResourceEnrollment({
        user_id,
        learning_resource_enrollment_id: enrollment.id,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.loadFormationProfile();
          this.snack_bar.open('Curso marcado como completado', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al marcar el curso como completado.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  openEditAchievementDialog(learning_resource_achievement: LearningResourceAchievement): void {
    const profile_user = this.profile_user();

    if (!profile_user) {
      return;
    }

    const dialog_ref = this.mat_dialog.open(LearningResourceAchievementFormDialogComponent, {
      width: '520px',
      minWidth: '400px',
      data: { mode: 'edit', user: profile_user, learning_resource_achievement },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.loadFormationProfile();
        }
      });
  }

  deleteAchievement(learning_resource_achievement: LearningResourceAchievement): void {
    const resource_name = learning_resource_achievement.learning_resource?.name ?? 'esta formación';

    if (!confirm(`¿Eliminar el logro de "${resource_name}"?`)) {
      return;
    }

    this.api_service
      .deleteLearningResourceAchievement(learning_resource_achievement.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.loadFormationProfile();
          this.snack_bar.open('Logro eliminado', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar el logro.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  private loadFormationProfile(): void {
    const user_id = this.user_id();

    this.loading.set(true);

    if (this.enable_achievement_management()) {
      this.achievements_displayed_columns = ['resource', 'period', 'completed_ago', 'actions'];
    } else {
      this.achievements_displayed_columns = ['resource', 'period', 'completed_ago'];
    }

    forkJoin({
      user: this.api_service.getUser(user_id.toString()),
      active_enrollments: this.api_service.getActiveLearningResourceEnrollmentsForUser(user_id),
      enrollments: this.api_service.getLearningResourceEnrollments({
        user_id,
        per_page: 100,
        order_by: 'start_date:desc',
        with: 'learning_resource',
        append: 'time_registered_in_seconds',
      }),
      achievements: this.api_service.getUserLearningResourceAchievements(user_id),
    })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: ({ user, active_enrollments, enrollments, achievements }): void => {
          this.profile_user.set(user);
          this.active_enrollments.set(active_enrollments);
          this.achievements_data_source.data = achievements;
          this.enrollments_data_source.data = enrollments.data.map(enrollment => this.serializeEnrollment(enrollment));
          this.loading.set(false);
          this.formation_profile_loaded.emit({ active_enrollments });
          this.change_detector_ref.markForCheck();
        },
        error: (): void => {
          this.loading.set(false);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private serializeEnrollment(enrollment: LearningResourceEnrollment): LearningResourceEnrollmentSerialized {
    const is_active = enrollment.end_date === null;
    const time_registered_in_seconds = enrollment.time_registered_in_seconds ?? 0;

    return {
      ...enrollment,
      resource_name: enrollment.learning_resource?.name ?? '-',
      is_active,
      formation_hours_label: this.formatSecondsAsHours(time_registered_in_seconds),
    };
  }

  private formatSecondsAsHours(seconds: number): string {
    const hours = seconds / 3600;

    return `${hours.toFixed(1)} h`;
  }
}
